import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastService } from '../../shared/services/toast.service';
import { SentryService } from '../services/sentry.service';

/** Auth-endpoint path prefix — errors here are the login/register component's responsibility. */
const AUTH_PATH = '/auth/';

/**
 * Endpoints whose adapters manage their own error UX — excluded to prevent
 * a second toast appearing alongside the adapter's own feedback.
 *
 * - `/attempts`     → `AttemptAdapter`   (`POST /process-server/{id}/attempts`)
 * - `/esignqueue`   → `AffidavitAdapter` (`POST /process-server/{id}/jobs/esignqueue`)
 * - `/acceptqueue`  → `JobsService.acceptJobs()` offline catch applies "✓ Accepted" UI;
 *                     `UpqueueAdapter` shows its own result toast on batch retransmit.
 * - `/jobs/upqueue` → `UpqueueAdapter.transmitAll()` shows its own success/failure toast.
 * - `/settings/app` → `AppSettingsService.load()` swallows errors; settings are non-critical.
 *
 * Note: the ticket named `/affidavits` but the actual affidavit endpoint path
 * is `/esignqueue` — verified against `affidavit.adapter.ts`.
 *
 * **`SyncService`'s own Common-API GETs** (`/jobs` job list, plus the 6
 * reference-data lookups fetched by `ReferenceDataService.prefetch()`) are
 * also excluded. Without this, every failed request in a single sync cycle
 * (login, auto-sync, or a manual refresh with several endpoints down at
 * once) produced its own toast — a burst of overlapping/racing toasts on
 * top of `BrandHeaderComponent`'s own accurate "Data refreshed" / "Unable to
 * refresh" toast for manual refresh, and unwanted noise during silent
 * background auto-sync. `JobsService`/`ReferenceDataService` already swallow
 * these errors internally (see their `loadJobs()`/`prefetch()` docs) and
 * `SyncService.manualRefresh()`'s boolean return now drives the one
 * consolidated toast the user actually sees.
 *
 * - `/documents/download` / `/documents/email` → `PrintQueueComponent` shows its own
 *   persistent-until-dismissed failure toast (`ToastService.errorUntilDismissed()`,
 *   see `onPrintComplete()`). Without this exclusion, a failed print/email
 *   request produced two overlapping toasts — this interceptor's own generic
 *   "Something went wrong..."/"No network connection..." toast racing against
 *   the print queue's friendly, user-facing message.
 */
const EXCLUDED_ROUTES: readonly string[] = [
  '/attempts',
  '/esignqueue',
  '/acceptqueue',
  '/jobs/upqueue',
  '/settings/app',
  '/jobs',
  '/hygiene-factor',
  '/relationship',
  '/bad-address-reason',
  '/observation',
  '/reason',
  '/esignature',
  '/documents/download',
  '/documents/email',
];

/**
 * Maps an HTTP status code to a user-friendly message and toast severity.
 *
 * @param status - HTTP status code from the failed response (0 = network/offline error).
 * @returns Plain-language message and the {@link ToastService} method to call.
 */
function toastForStatus(status: number): {
  message: string;
  level: 'warning' | 'error' | 'info';
} {
  switch (status) {
    case 400:
      return {
        message: 'Invalid request. Please check your input and try again.',
        level: 'warning',
      };
    case 403:
      return {
        message: 'You do not have permission to perform that action.',
        level: 'warning',
      };
    case 404:
      return {
        message: 'The requested resource could not be found.',
        level: 'warning',
      };
    case 408:
      return {
        message: 'The request timed out. Check your connection and try again.',
        level: 'warning',
      };
    case 429:
      return {
        message: 'Too many requests. Please wait a moment and try again.',
        level: 'warning',
      };
    case 500:
      return {
        message: 'Something went wrong on the server. Please try again.',
        level: 'error',
      };
    case 502:
    case 503:
    case 504:
      return {
        message: 'The server is temporarily unavailable. Please try again shortly.',
        level: 'error',
      };
    case 0:
      return {
        message: 'No network connection. Please try again when connected.',
        level: 'info',
      };
    default:
      return {
        message: 'An unexpected error occurred. Please try again.',
        level: 'error',
      };
  }
}

/**
 * Angular HTTP interceptor that catches all API errors and surfaces
 * user-friendly messages via {@link ToastService}.
 *
 * @remarks
 * **Sentry capture:** All HTTP 5xx responses are forwarded to Sentry via
 * {@link SentryService} regardless of the toast exclusion rules below.
 *
 * **Toast skipped entirely for:**
 * - HTTP 401 — handled by `authInterceptor` (refresh → retry → session-expiry toast).
 * - Auth-endpoint paths (`/auth/`) — login / register errors are handled by
 *   the calling component.
 * - Endpoints in {@link EXCLUDED_ROUTES} — their adapters already show their own
 *   feedback (`/attempts`, `/esignqueue`, `/acceptqueue`, `/jobs/upqueue`).
 *
 * **Toast severity by status:**
 * | Codes                    | Colour  | Method                   |
 * |--------------------------|---------|--------------------------|
 * | 400 403 404 408 429      | Warning | `toastService.warning()` |
 * | 500 502 503 504 unknown  | Danger  | `toastService.error()`   |
 * | 0 / offline              | Dark    | `toastService.info()`    |
 *
 * The error is always re-thrown so callers can still handle it (e.g. set a
 * component error state). No raw status code, error object, or stack trace is
 * ever shown to the process server.
 *
 * @param req  - The outgoing HTTP request.
 * @param next - The next handler in the interceptor chain.
 */
export const globalErrorHandlerInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService  = inject(ToastService);
  const sentryService = inject(SentryService);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && err.status >= 500) {
        sentryService.captureException(err, { url: req.url, status: err.status });
      }

      if (
        err instanceof HttpErrorResponse &&
        err.status !== 401 &&
        !req.url.includes(AUTH_PATH) &&
        !EXCLUDED_ROUTES.some((route) => req.url.includes(route))
      ) {
        const { message, level } = toastForStatus(err.status);
        if (level === 'warning') { void toastService.warning(message); }
        else if (level === 'info') { void toastService.info(message); }
        else { void toastService.error(message); }
      }
      return throwError(() => err);
    }),
  );
};
